import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// edit payment voucher
export const editPaymentVoucher = createAsyncThunk(
  "PaymentVouchers/editPaymentVoucher",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }

      let payload;
      let headers = {
        Authorization: `Bearer ${token}`,
        "Accept-Language": currentLang,
      };

      if (data instanceof FormData) {
        data.append("_method", "PUT");
        payload = data;
      } else {
        payload = {
          ...data,
          _method: "PUT",
          total_amount:
            data.total_amount !== undefined
              ? Number(data.total_amount)
              : undefined,
          details:
            typeof data.details === "string"
              ? data.details
              : JSON.stringify(data.details || []),
        };
        headers["Content-Type"] = "application/json";
      }

      const response = await axios.post(
        `${BASE_URL}/payment-vouchers/${id}`,
        payload,
        {
          headers,
        },
      );
      return response.data;
    } catch (error) {
      let message = "Failed to edit payment voucher!";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          message = errorData;
        } else if (errorData.message) {
          message = errorData.message;
        } else if (typeof errorData === "object") {
          message = JSON.stringify(errorData);
        }
      }
      return rejectWithValue(message);
    }
  },
);

// Payment Vouchers
export const getPaymentVouchers = createAsyncThunk(
  "PaymentVouchers/getPaymentVouchers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search !== undefined)
        queryParams.append("search", params.search);
      if (params.project_id !== undefined)
        queryParams.append("project_id", params.project_id);
      if (params.account_id !== undefined)
        queryParams.append("account_id", params.account_id);
      if (params.status !== undefined)
        queryParams.append("status", params.status);
      if (params.date_to !== undefined)
        queryParams.append("date_to", params.date_to);
      if (params.date_from !== undefined)
        queryParams.append("date_from", params.date_from);
      if (params.sort_by !== undefined)
        queryParams.append("sort_by", params.sort_by);
      if (params.sort_order !== undefined)
        queryParams.append("sort_order", params.sort_order);
      const url = `${BASE_URL}/payment-vouchers${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get payment vouchers!";
      return rejectWithValue(message);
    }
  },
);

// get payment voucher details
export const getPaymentVoucherDetails = createAsyncThunk(
  "PaymentVouchers/getPaymentVoucherDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.get(`${BASE_URL}/payment-vouchers/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to get payment voucher details!";
      return rejectWithValue(message);
    }
  },
);

// add payment voucher
export const addPaymentVoucher = createAsyncThunk(
  "PaymentVouchers/addPaymentVoucher",
  async (voucherData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const isFormData = voucherData instanceof FormData;
      const headers = {
        Authorization: `Bearer ${token}`,
        "Accept-Language": currentLang,
      };

      if (!isFormData) {
        headers["Content-Type"] = "application/json";
        const payload = {
          ...voucherData,
          total_amount:
            voucherData.total_amount !== undefined
              ? Number(voucherData.total_amount)
              : undefined,
          details:
            typeof voucherData.details === "string"
              ? voucherData.details
              : JSON.stringify(voucherData.details || []),
        };
        const response = await axios.post(
          `${BASE_URL}/payment-vouchers`,
          payload,
          {
            headers,
          },
        );
        return response.data;
      }

      const response = await axios.post(
        `${BASE_URL}/payment-vouchers`,
        voucherData,
        {
          headers,
        },
      );
      return response.data;
    } catch (error) {
      let message = "Failed to add payment voucher!";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          message = errorData;
        } else if (errorData.message) {
          message = errorData.message;
        } else if (typeof errorData === "object") {
          message = JSON.stringify(errorData);
        }
      }
      return rejectWithValue(message);
    }
  },
);

// post payment voucher
export const postPaymentVoucher = createAsyncThunk(
  "PaymentVouchers/postPaymentVoucher",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.post(
        `${BASE_URL}/payment-vouchers/${id}/post`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to post payment voucher!";
      return rejectWithValue(message);
    }
  },
);

// delete payment voucher
export const deletePaymentVoucher = createAsyncThunk(
  "PaymentVouchers/deletePaymentVoucher",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.delete(
        `${BASE_URL}/payment-vouchers/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete payment voucher!";
      return rejectWithValue(message);
    }
  },
);
